import MetricsCardWrap from '@/pages/dashboard/components/MetricsCardWrap';
import React, { useMemo, useState } from 'react';
import { Switch, Radio } from 'tea-component';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import { getName } from '@home/utils/common';
import classNames from 'classnames';
import '@/pages/dashboard/components/Plugin/index.scss';
import EventMetricTrend from '@/pages/dashboard/components/EventMetricTrend';
import { downloadExcel } from '@/pages/dashboard/utils/download/index';
import { formatEventDataSource } from '@/pages/dashboard/components/EventMetricTrend/utils';
import TipContext from '@home/components/TipContext';
import type { RadioOption, PropsEventData } from '@/pages/dashboard/components/EventMetricTrend/types';
import ViewportFullscreenShell from '@/pages/dashboard/components/ViewportFullscreen';
import './index.scss';

/**
 * 日期格式化：将 "20250301" 转为 "2025-03-01"
 */
const formatDate = (str: string): string => {
  if (!str || str.length !== 8) return str;
  return `${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`;
};

/**
 * 转换单条趋势数据：将通用 field/value/last_year_data 格式映射为命名字段格式
 * 输入: { field: "active_ret_d2", value: "0.45", last_year_data: "0.42", ... }
 * 输出: { active_ret_d2: "0.45", last_year_active_ret_d2: "0.42", ... }
 */
/**
 * 安全转数值：空字符串返回 undefined（ECharts 跳过空数据点），有值则转为 Number
 */
const toNumericValue = (val: any): number | undefined => {
  if (val === '' || val === null || val === undefined) return undefined;
  const num = Number(val);
  return isNaN(num) ? undefined : num;
};

const transformTrendItem = (item: any): any => {
  const fieldName = item?.field;
  if (!fieldName) return item;

  const transformed: any = {
    date: item.date,
    last_year_date: item.last_year_date,
    last_year_version_date: item.last_year_version_date,
    last_week_date: item.last_week_date,
    is_abnormal: item.is_abnormal,
    [fieldName]: toNumericValue(item.value),
  };

  const lastYearVal = toNumericValue(item.last_year_data);
  if (lastYearVal !== undefined) {
    transformed[`last_year_${fieldName}`] = lastYearVal;
  }
  const lastYearVersionVal = toNumericValue(item.last_year_version_data);
  if (lastYearVersionVal !== undefined) {
    transformed[`last_year_version_${fieldName}`] = lastYearVersionVal;
  }
  const lastWeekVal = toNumericValue(item.last_week_data);
  if (lastWeekVal !== undefined) {
    transformed[`last_week_${fieldName}`] = lastWeekVal;
  }

  return transformed;
};

/**
 * 单个趋势图配置
 */
interface ITrendChartConfig {
  titleCn: string;
  titleEn: string;
  /** 该图对应的 Service dataKey，每个图独立调用接口 */
  apiDataKey: string;
  /** 在 API 响应中的数据 key 数组，多指标图传多个 key */
  trendDataKeys: string[];
  seriesConfig: any[];
  chartConfig: any;
  aiTag?: string;
  aiTagMap?: Record<string, string | undefined>;
  enableAlignmentAiDataKey?: boolean;
  alignmentDataKeyMap?: Record<string, string | undefined>;
  radioConfig?: any;
  downloadConfig?: {
    columns?: any[];
  };
  /** 周粒度图表：下载时将事件归并到所属周刻度 */
  weeklyEventMerge?: boolean;
  /** 为 true 时由 ViewportFullscreenShell 包裹并注入视口全屏能力（pageConfig 与「用户质量分析」多图） */
  enableFullScreen?: boolean;
  /** 全屏时内容字号放大倍数，默认 1.4 */
  fullscreenScale?: number;
  /** 全屏时 X 轴字号放大倍数；未设则与 fullscreenScale 一致 */
  fullscreenXAxisScale?: number;
  [key: string]: unknown;
}

/**
 * ChangeAnalysisMultiTrendPanel Props
 */
interface IMultiTrendPanelProps {
  titleCn: string;
  titleEn: string;
  className?: string;
  style?: React.CSSProperties;
  data: any;
  /** 事件数据 key，默认 'eventData' */
  eventKey?: string;
  showEventSwitch?: boolean;
  defaultShowEvent?: boolean;
  /** 共享的版本对齐/自然日 Radio 配置 */
  radioConfig?: any;
  /** 4 个趋势图配置（每个有独立的 apiDataKey） */
  trendCharts: ITrendChartConfig[];
  /** 下载文件名 */
  downloadFileName?: string;
  /**
   * 全屏时 zoom 放大倍数，面板内所有子图共享；子图可通过 ITrendChartConfig.fullscreenScale 单独覆盖。
   * 默认 1.4。
   */
  fullscreenScale?: number;
  /** 全屏时 X 轴字号放大倍数，面板内子图共享；子图可单独覆盖 */
  fullscreenXAxisScale?: number;
  [key: string]: unknown;
}

const ChangeAnalysisMultiTrendPanel: React.FC<IMultiTrendPanelProps> = (props) => {
  const {
    titleCn,
    titleEn,
    className,
    style,
    data,
    eventKey = 'eventData',
    showEventSwitch = true,
    defaultShowEvent = true,
    radioConfig,
    trendCharts = [],
    downloadFileName,
    fullscreenScale: panelFullscreenScale = 1,
    fullscreenXAxisScale: panelFullscreenXAxisScale,
  } = props;

  const { isEN } = useLocal();
  const { formatMessage } = useLocal();

  // ===== 共享状态 =====
  const [showEvent, setShowEvent] = useState<boolean>(defaultShowEvent);
  const [selectedRadioValue, setSelectedRadioValue] = useState<string>(
    radioConfig?.defaultValue || radioConfig?.options?.[0]?.value || ''
  );

  const title = getName(isEN, titleEn, titleCn);

  // ===== 合并趋势数据：从指定 apiDataKey 读取并转换 + 合并 =====
  const mergeChartData = (chart: ITrendChartConfig): { xAxisData: string[]; seriesData: any[]; } => {
    const chartApiData = data?.[chart.apiDataKey]?.data;
    const allArrays = chart.trendDataKeys.map((key: string) =>
      (chartApiData?.[key] || []).map(transformTrendItem)
    );
    if (allArrays.length === 0 || allArrays[0]?.length === 0) {
      return { xAxisData: [], seriesData: [] };
    }

    const mergedData = allArrays[0].map((item: any, index: number) => {
      let merged = { ...item };
      for (let i = 1;i < allArrays.length;i++) {
        if (allArrays[i]?.[index]) {
          merged = { ...merged, ...allArrays[i][index] };
        }
      }
      return merged;
    });

    return {
      xAxisData: mergedData.map((item: any) => formatDate(item.date)),
      seriesData: mergedData.map((item: any) => ({
        ...item,
        date: formatDate(item.date),
        last_year_version_date: item.last_year_version_date
          ? formatDate(item.last_year_version_date)
          : undefined,
        last_year_date: item.last_year_date
          ? formatDate(item.last_year_date)
          : undefined,
      })),
    };
  };

  /**
   * 将按天的事件数据归并到周刻度上
   * 每天的事件归并到它所属的那个周刻度（<= 事件日期的最大周刻度）
   */
  const mergeEventDataToWeeklyDates = (chartData: { xAxisData: string[]; seriesData: any[]; }): any => {
    const rawEventData = data?.[eventKey];
    if (!rawEventData?.data || !chartData?.xAxisData?.length) return rawEventData;

    // 周刻度日期数组（无横线格式，已排序），如 ["20251012", "20251019", "20251026", ...]
    const weekDatesCompact = chartData.xAxisData
      .map((d: string) => d.replace(/-/g, ''))
      .sort();

    // 找到日期所属的周刻度：>= eventDate 的最小周刻度（事件归到所属周的周日）
    const findWeekDate = (eventDateKey: string): string | null => {
      for (const weekDate of weekDatesCompact) {
        if (weekDate >= eventDateKey) {
          return weekDate;
        }
      }
      return null;
    };

    // 归并事件到周刻度，合并同一周的事件并去重
    const mergeToWeekly = (sourceEventData: any): any => {
      if (!sourceEventData) return {};
      const merged: any = {};
      Object.keys(sourceEventData).forEach((dayKey: string) => {
        const weekKey = findWeekDate(dayKey);
        if (!weekKey) return;
        if (!merged[weekKey]) {
          merged[weekKey] = [];
        }
        const existingNames = new Set(merged[weekKey].map((e: any) => e.name));
        sourceEventData[dayKey].forEach((event: any) => {
          if (!existingNames.has(event.name)) {
            merged[weekKey].push(event);
            existingNames.add(event.name);
          }
        });
      });
      return merged;
    };

    const originalData = rawEventData.data;
    return {
      ...rawEventData,
      data: {
        ...originalData,
        eventData: mergeToWeekly(originalData?.eventData),
        lastEventData: mergeToWeekly(originalData?.lastEventData),
      },
    };
  };

  // ===== 为每个子图构造 EventMetricTrend 所需的 data 结构 =====
  const createChartData = (chart: ITrendChartConfig): any => {
    const chartServiceData = data?.[chart.apiDataKey] || {};
    const chartData = mergeChartData(chart);
    return {
      trendData: {
        data: chartData,
        errorMsg: chartServiceData.errorMsg,
        loading: chartServiceData.loading,
        req: chartServiceData.req,
      },
      [eventKey]: mergeEventDataToWeeklyDates(chartData),
      gameCode: data?.gameCode,
    };
  };

  // ===== 是否有数据（任一图有数据即显示下载按钮） =====
  const hasAnyData = useMemo(() => {
    return trendCharts.some((chart: ITrendChartConfig) => {
      const chartApiData = data?.[chart.apiDataKey]?.data;
      return chart.trendDataKeys.some((key: string) => chartApiData?.[key]?.length > 0);
    });
  }, [data, trendCharts]);

  // ===== 多 Sheet Excel 下载 =====
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const handleDownload = async (): Promise<void> => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      // 获取事件原始列表，用于下载时匹配事件信息
      const propsEventData = data?.[eventKey]?.data as PropsEventData | undefined;
      const rawEventList = propsEventData?.rawEventList;

      const tabs = trendCharts
        .filter((chart: ITrendChartConfig) => chart.downloadConfig?.columns?.length)
        .map((chart: ITrendChartConfig) => {
          const chartData = mergeChartData(chart);
          // 根据当前 Radio 值过滤下载列
          const columns = (chart.downloadConfig?.columns || []).filter((col: any) => {
            if (!col.radioValue) return true;
            return col.radioValue === selectedRadioValue;
          });
          // 将事件信息合并到下载数据中
          let dataSource = chartData.seriesData || [];
          if (rawEventList && rawEventList.length > 0 && chart.downloadConfig) {
            let eventListForDownload = rawEventList as any[];

            // 周粒度图表：直接用图表展示用的已归并 eventData 来构建下载事件
            if (chart.weeklyEventMerge) {
              const mergedEventWrapper = mergeEventDataToWeeklyDates(chartData);
              const mergedEvents = mergedEventWrapper?.data?.eventData || {};
              // 将归并后的 { weekDate: [events] } 转为 rawEventList 格式
              eventListForDownload = [];
              Object.keys(mergedEvents).forEach((weekKey: string) => {
                const weekDateFormatted = weekKey.length === 8
                  ? `${weekKey.slice(0, 4)}-${weekKey.slice(4, 6)}-${weekKey.slice(6, 8)}`
                  : weekKey;
                (mergedEvents[weekKey] || []).forEach((ev: any) => {
                  eventListForDownload.push({
                    ...ev,
                    start_time: weekDateFormatted,
                    // buildEventData 会删除原始字段并重命名，需要还原给 formatEventDataSource 使用
                    event_title_cn: ev.event_title_cn || ev.name_cn || ev.name || '',
                    event_title_en: ev.event_title_en || ev.name || ev.name_cn || '',
                  });
                });
              });
            }

            dataSource = formatEventDataSource(
              dataSource,
              eventListForDownload as any,
              chart.downloadConfig as any,
              isEN,
            ) as any[];
          }
          return {
            tabName: getName(isEN, chart.titleEn, chart.titleCn),
            dataSource,
            columns,
          };
        });

      if (tabs.length > 0) {
        await downloadExcel({
          fileName: downloadFileName || title,
          tabs,
        });
      }
    } catch (error) {
      console.error('[ChangeAnalysisMultiTrendPanel] download failed:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <MetricsCardWrap
      titleCn=""
      titleEn=""
      className={classNames('dbd-change-analysis-multi-trend-panel', className)}
      style={style}
      cardHeader={
        <div className="dbd-multi-trend-panel-header">
          <div className="dbd-multi-trend-panel-left">
            <div className="dbd-multi-trend-panel-title">{title}</div>

            {showEventSwitch && (
              <div className="dbd-multi-trend-panel-switch">
                <Switch
                  defaultValue={defaultShowEvent}
                  onChange={(value: boolean) => setShowEvent(value)}
                >
                  {getName(isEN, 'Event', '事件')}
                </Switch>
              </div>
            )}

            {radioConfig && radioConfig.options?.length > 0 && (
              <Radio.Group
                className="dbd-multi-trend-panel-radio"
                value={selectedRadioValue}
                onChange={(value: string) => setSelectedRadioValue(value)}
              >
                {radioConfig.options.map((option: RadioOption) => (
                  <Radio key={option.value} name={option.value}>
                    {getName(isEN, option.labelEn, option.labelCn)}
                  </Radio>
                ))}
              </Radio.Group>
            )}
          </div>

          <div className="dbd-multi-trend-panel-operation">
            {hasAnyData && (
              <TipContext
                tipText={formatMessage({ id: 'dbl_common_download' })}
                type={{ default: 'download', hover: 'download' }}
                onClick={handleDownload}
                className={classNames({ 'is-downloading': isDownloading })}
              />
            )}
          </div>
        </div>
      }
    >
      {/* 2x2 趋势图网格 */}
      <div className="dbd-multi-trend-grid">
        {trendCharts.map((chart: ITrendChartConfig) => {
          const trendProps = {
            titleCn: chart.titleCn,
            titleEn: chart.titleEn,
            className: 'dbd-multi-trend-grid-item',
            hideControls: true,
            controlledShowEvent: showEvent,
            controlledRadioValue: selectedRadioValue,
            aiTag: chart.aiTag,
            aiTagMap: chart.aiTagMap,
            enableAlignmentAiDataKey: chart.enableAlignmentAiDataKey,
            alignmentDataKeyMap: chart.alignmentDataKeyMap,
            data: createChartData(chart),
            apiDataKey: 'trendData',
            eventKey,
            seriesConfig: chart.seriesConfig,
            radioConfig: chart.radioConfig || radioConfig,
            chartConfig: chart.chartConfig,
            showEventSwitch: false,
            defaultShowEvent: showEvent,
            downloadConfig: chart.downloadConfig,
            ...(chart.yAxisMin ? { yAxisMin: chart.yAxisMin as any } : {}),
          };

          const chartEl = <EventMetricTrend {...trendProps} />;

          if (!chart.enableFullScreen) {
            return (
              <React.Fragment key={chart.apiDataKey}>
                {chartEl}
              </React.Fragment>
            );
          }
          return (
            <ViewportFullscreenShell
              key={chart.apiDataKey}
              enabled={true}
              className="dbd-multi-trend-shell-cell"
              layerClassName="dbd-multi-trend-fs-root dbd-multi-trend-fs-root--active"
              backdropClassName="dbd-multi-trend-fs-backdrop"
              placeholderClassName="dbd-multi-trend-fs-placeholder"
              placeholderMinHeight={420}
              fullscreenScale={chart.fullscreenScale ?? panelFullscreenScale}
              fullscreenXAxisScale={
                chart.fullscreenXAxisScale ?? panelFullscreenXAxisScale
              }
            >
              {chartEl}
            </ViewportFullscreenShell>
          );
        })}
      </div>
    </MetricsCardWrap>
  );
};

export default ChangeAnalysisMultiTrendPanel;
